import { Col, Form, message, Row } from "antd";
import dayjs, { type ManipulateType } from "dayjs";
import React, { useEffect } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAxiosError } from "../../../../../../api";
import { NextIcon } from "../../../../../../assets/svg";
import type { AppDispatch } from "../../../../../../redux/store";
import { ReactSelectField } from "../../../../../atoms/FormElement";
import { InputNumberFieldV2 as InputNumberField } from "../../../../../atoms/FormElement/InputNumberField";
import { extractValue } from "../../../../../libs/utils";
import { AppSegmented, type AppSegmentedOption } from "../../../../../molecules/AppSegmented";
import { StepCard, type StepCardAction } from "../../../../../molecules/StepCard";
import { resolveBreakdownMode } from "../../../../BookingForm/components/Commercial/add-plan";
import { SecurityDepositTopUp } from "../../../../BookingForm/components/modalContent/security-deposit-top-up";
import { apiDateFormat } from "../../../../BookingForm/components/utils";
import { getBookingDetail } from "../../../../BookingForm/ducks/actions";
import { useBookingSelector } from "../../../../BookingForm/hooks/useBookingSelector";
import { type SaveCommercialRenewalPayload, useSaveCommercialRenewal } from "../../api";
import type { CommercialRenewalStepProps } from "../types";
import { didRenewalPaymentPlanConfigChange } from "./didRenewalPaymentPlanConfigChange";
import { type RenewalTermsFormValues, useRenewalTermsForm } from "./useRenewalTermsForm";
import { BASE_RENT_ONLY } from "../../../../BookingForm/hooks/useTorRules";

export const BREAKDOWN_MODE_OPTIONS: AppSegmentedOption[] = [
  { label: "Billing Line", value: "billingSchedule" },
  { label: "Schedule", value: "paymentSchedule" },
];

const DURATION_UNIT_MAP: Record<string, ManipulateType> = {
  days: "day",
  months: "month",
  years: "year",
};

const getRenewalLeaseTerms = (props: {
  currentLeaseEndDate?: string;
  durationType: string;
  numberOfRenewalTerms: number;
}) => {
  const { currentLeaseEndDate, durationType, numberOfRenewalTerms } = props;
  const unit = DURATION_UNIT_MAP[durationType];
  if (!currentLeaseEndDate || !unit) return null;

  const leaseStart = dayjs(currentLeaseEndDate).add(1, "day");
  if (!leaseStart.isValid()) return null;

  const leaseEnd = leaseStart.add(numberOfRenewalTerms, unit).subtract(1, "day");
  const leaseStartDate = apiDateFormat(leaseStart);
  const leaseEndDate = apiDateFormat(leaseEnd);

  return {
    leaseStartDate,
    leaseEndDate,
    commencementDate: leaseStartDate,
  };
};

const mapRenewalTermsToPayload = (
  data: RenewalTermsFormValues,
  currentLeaseEndDate?: string,
): SaveCommercialRenewalPayload | null => {
  const torType = extractValue(data.torRuleType);
  const durationType = extractValue(data.period);
  const numberOfRenewalTerms = Number(data.renewFor);

  if (!torType || !durationType || !Number.isFinite(numberOfRenewalTerms) || numberOfRenewalTerms < 1) return null;

  const leaseTerms = getRenewalLeaseTerms({ currentLeaseEndDate, durationType, numberOfRenewalTerms });
  if (!leaseTerms) return null;

  const securityDepositTopUp =
    data.isTopUpRequired && data.securityDepositTopUp
      ? {
          currentAmount: Number(data.securityDepositTopUp.currentAmount ?? 0),
          requiredPercentage: data.securityDepositTopUp.requiredPercentage ?? 0,
          requiredAmount: Number(data.securityDepositTopUp.requiredAmount ?? 0),
        }
      : undefined;

  const generate = torType === BASE_RENT_ONLY && data.breakdownMode ? data.breakdownMode : undefined;

  return {
    torType,
    ...(generate ? { generate } : {}),
    bookingTerms: {
      durationType,
      numberOfRenewalTerms,
      ...(securityDepositTopUp ? { securityDepositTopUp } : {}),
    },
    leaseTerms,
  };
};

export const RenewalTermsStep = (props: CommercialRenewalStepProps) => {
  const { title, onDiscard, onNext, onBack, onInvalidatePaymentPlans, isSentForApproval } = props;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const bookingDetails = useBookingSelector();
  const { formValues, leaseId, leaseEndDate, torRulesOptions, periodOptions } = useRenewalTermsForm();

  const form = useForm<RenewalTermsFormValues>({ defaultValues: formValues });
  const { control, handleSubmit, reset, clearErrors, setValue } = form;

  const torRuleType = useWatch({ control, name: "torRuleType" });
  const isBreakdownModeVisible = torRuleType?.value === BASE_RENT_ONLY;

  const { saveCommercialRenewalRequest, isPendingSaveCommercialRenewalRequest } = useSaveCommercialRenewal({
    onError: (error) => message.error(getAxiosError(error, "Failed to save renewal terms")),
  });

  const isSaving = isPendingSaveCommercialRenewalRequest;

  useEffect(() => {
    if (!leaseId) return;
    reset(formValues);
  }, [leaseId, formValues, reset]);

  useEffect(() => {
    if (isBreakdownModeVisible) return;
    clearErrors("breakdownMode");
  }, [isBreakdownModeVisible, clearErrors]);

  const saveRenewalTerms = async (data: RenewalTermsFormValues) => {
    if (!leaseId) {
      message.error("Unable to save renewal terms because lease details are missing");
      return false;
    }

    if (isBreakdownModeVisible && !data.breakdownMode) {
      message.error("Please select a payment breakdown mode");
      return false;
    }

    const payload = mapRenewalTermsToPayload(data, leaseEndDate);
    if (!payload) {
      message.error("Please complete all required renewal terms");
      return false;
    }

    // Capture before API + refetch so compare doesn't use post-save bookingDetails.
    const previousTorType = bookingDetails?.torType;
    const previousBreakdownMode = resolveBreakdownMode(bookingDetails?.generate);
    const nextBreakdownMode = payload.torType === BASE_RENT_ONLY ? resolveBreakdownMode(payload.generate) : undefined;

    try {
      const response = await saveCommercialRenewalRequest({ leaseId, payload });
      const newLeaseId = response?.result?.leaseId;
      if (!newLeaseId) {
        message.error("Renewal was created but lease id is missing from the response");
        return false;
      }

      await dispatch(getBookingDetail(newLeaseId));

      // Refetch may restore API payment plans; invalidate when lease type / breakdown mode changed.
      const changed = didRenewalPaymentPlanConfigChange({
        previousTorType,
        nextTorType: payload.torType,
        previousBreakdownMode,
        nextBreakdownMode,
      });
      if (changed) onInvalidatePaymentPlans?.();

      if (newLeaseId !== leaseId) {
        navigate(`/renewals/${newLeaseId}`, { replace: true });
      }
      if (response?.message) message.success(response.message);
      return true;
    } catch {
      return false;
    }
  };

  const onSaveDraft = handleSubmit(async (data) => {
    await saveRenewalTerms(data);
  });

  const onSubmitNext = isSentForApproval
    ? () => onNext?.()
    : handleSubmit(async (data) => {
        const ok = await saveRenewalTerms(data);
        if (!ok) return;
        onNext?.();
      });

  const actions: StepCardAction[] = [
    {
      key: "discard",
      label: "Discard",
      variant: "secondary",
      onClick: onDiscard,
      disabled: isSaving,
    },
    ...(isSentForApproval
      ? []
      : [
          {
            key: "draft",
            label: "Save Draft",
            variant: "subtle" as const,
            onClick: onSaveDraft,
            disabled: isSaving,
          },
        ]),
    {
      key: "next",
      label: "Next",
      variant: "primary",
      icon: <NextIcon width="16px" height="16px" />,
      iconPosition: "end",
      onClick: onSubmitNext,
      disabled: isSaving,
    },
  ];

  return (
    <StepCard title={title} onBack={onBack} actions={actions}>
      <FormProvider {...form}>
        <Form layout="vertical" className="bf-main">
          <Row gutter={12}>
            <Col span={4}>
              <InputNumberField
                isRequired
                control={control}
                label="Renew For"
                fieldname="renewFor"
                stringMode={false}
                inputNumberProps={{
                  min: 1,
                  style: { borderRadius: 6 },
                }}
              />
            </Col>
            <Col span={4}>
              <ReactSelectField
                control={control}
                label="Period"
                fieldname="period"
                selectOption={periodOptions}
                iProps={{ placeholder: "Select" }}
              />
            </Col>
            <Col span={8}>
              <ReactSelectField
                isRequired
                control={control}
                setValue={setValue}
                label="Lease Type"
                fieldname="torRuleType"
                selectOption={torRulesOptions}
                iProps={{ placeholder: "Select" }}
              />
            </Col>
            {isBreakdownModeVisible ? (
              <Col span={8}>
                <Form.Item label="Payment Breakdown Mode" required>
                  <Controller
                    name="breakdownMode"
                    control={control}
                    rules={{ required: "Payment breakdown mode is required" }}
                    render={({ field }) => (
                      <AppSegmented
                        options={BREAKDOWN_MODE_OPTIONS}
                        value={field.value ?? "billingSchedule"}
                        onChange={(value) => field.onChange(value)}
                      />
                    )}
                  />
                </Form.Item>
              </Col>
            ) : null}
          </Row>
          <SecurityDepositTopUp />
        </Form>
      </FormProvider>
    </StepCard>
  );
};
